import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { Box, Grid2 as Grid, type Theme } from '@mui/material';
import { appBarHeight } from '@/components/AppBar/AppBar.style';
import { footerHeight } from '@/components/Footer';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { useGetWatchList } from '@/pages/ValuationsV2/hooks/useGetWatchList';
import { combineSx } from '@/utils/combineSx';
import { AssetDetails } from './components/AssetDetails';
import { AssetList } from './components/AssetList/AssetList';
import { Filters } from './components/Filters';
import { useGetAssetsList } from './hooks/useGetAssetsList';
import { useSelectedAssetDetails } from './hooks/useSelectedAssetDetails';
import { cardVariants } from './theme';

const valuationsStyles = {
  root: {
    height: `calc(100dvh - ${appBarHeight} - ${footerHeight})`,
    '--gap-section': '0.25rem',
    '--gap-sections': 'calc(var(--gap-section) * 3)',
    background: (theme: Theme) => theme.palette.primaryBackground.backgroundColor,
  },
  valuationsMain: {
    minHeight: 0,
    zIndex: '10',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: 'clamp(12rem, 25dvw, 20rem) auto',
    gap: 'var(--gap-sections)',
    p: {
      xs: '0 0 0 0.5rem',
      md: '0 0 0 0.625rem',
    },
  },
  assetDetailsWrapper: {
    display: 'grid',
    gap: 'var(--gap-sections)',
    overflow: 'auto',
    height: '100%',
    width: '100%',
    gridTemplateRows: 'auto 1fr',
    p: {
      xs: '0.5rem 0.5rem 0.5rem 0',
      md: '0.625rem 0.625rem 0.625rem 0',
    },
  },
  assetListContainer: [
    {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      margin: {
        xs: '0.5rem 0 0.5rem 0',
        md: '0.625rem 0 0.625rem 0',
      },
    },
    cardVariants({ interactive: false, padding: 'none', border: true }),
  ],
};

const AssetListWrapper = () => {
  const { loadMoreAssets } = useGetAssetsList();
  useGetWatchList();
  return <AssetList onLoadMore={loadMoreAssets} />;
};

const AssetDetailsWrapper = () => {
  useSelectedAssetDetails();
  return <AssetDetails />;
};

const Valuations = () => {
  return (
    <Grid
      container
      direction='column'
      flexWrap='nowrap'
      sx={valuationsStyles.root}
      data-id='valuations-layout'
    >
      <Filters />
      <Box component='main' sx={valuationsStyles.valuationsMain} data-id='valuations-main'>
        <Box
          sx={combineSx(valuationsStyles.assetListContainer)}
          data-id='asset-list-container'
          component='aside'
        >
          <AssetListWrapper />
        </Box>
        <Box
          component='section'
          sx={valuationsStyles.assetDetailsWrapper}
          data-id='asset-details-wrapper'
        >
          <AssetDetailsWrapper />
        </Box>
      </Box>
    </Grid>
  );
};

const ValuationsV2 = () => {
  const { valuationsV2 } = useGetFeatureFlags();

  if (!valuationsV2) {
    return null;
  }

  return <Valuations />;
};

if (process.env.NODE_ENV === 'development') {
  loadDevMessages();
  loadErrorMessages();
}

export default ValuationsV2;
